import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faBuilding } from '@fortawesome/free-solid-svg-icons';
import { closingDateBadge } from '../closingDateBadge';
import { clientShort } from '../clientShort';
import VagueBadge from './VagueBadge';

const DEPT_STYLE = {
  define:    { bg: '#e8f0fe', color: '#1967d2', label: 'Define' },
  voa:       { bg: '#fef7cd', color: '#a16207', label: 'Voa' },
  immersion: { bg: '#fce8e6', color: '#c5221f', label: 'Immersion' },
};

const SlotCard = ({ slot, project, accentColor, onClick, isDragOverlay }) => {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({
    id: `slot-${slot.id}`,
    data: { slot, project, type: 'slot' },
    disabled: isDragOverlay,
  });

  const style = isDragOverlay ? {} : {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
  };

  const isLinked = !!slot.survey_id;
  const isCati = slot.survey && slot.survey.poste_qcode !== 'ref';

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`project-card${isDragOverlay ? ' drag-overlay-card' : ''}`}
      onClick={() => onClick && onClick()}
    >
      {/* Badges: department + type */}
      <div className="project-card-badges">
        {project?.department && (() => {
          const dept = DEPT_STYLE[project.department] || DEPT_STYLE.immersion;
          return <span className="badge-dept" style={{ background: dept.bg, color: dept.color }}>{dept.label}</span>;
        })()}
        {isLinked && slot.survey ? (
          <span style={{
            fontSize: '0.6rem', fontWeight: 600, padding: '2px 7px', borderRadius: 3,
            background: isCati ? '#f3e8ff' : '#dbeafe',
            color: isCati ? '#7c3aed' : '#0284c7',
          }}>
            {isCati ? 'CATI' : 'F2F'}
          </span>
        ) : null}
        <VagueBadge wave={slot.survey_wave} style={{ fontSize: '0.6rem', padding: '2px 7px' }} />
        {(() => {
          const b = closingDateBadge(slot, { withStep: true });
          return b ? <span className={b.blink ? 'closing-badge-blink' : ''} style={{ fontSize: '0.6rem', fontWeight: 600, padding: '2px 7px', borderRadius: 3, background: b.bg, color: b.color, marginLeft: 'auto' }}>{b.text}</span> : null;
        })()}
      </div>

      {/* Name */}
      <div className="project-card-name">
        {project?.client?.name && <span style={{ color: '#9e9e9e', fontWeight: 600 }}>{clientShort(project.client.name, project.client.abbreviation)} - </span>}{slot.name}
      </div>

      {/* Survey title */}
      <div style={{ fontSize: '0.68rem', color: '#9e9e9e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minHeight: '1em' }}>
        {isLinked && slot.survey ? (
          <>
            {slot.survey.title} <span style={{ color: '#bdbdbd' }}>({slot.survey.limesurvey_id})</span>
            {(slot.linked_surveys?.length || 0) > 1 && (
              <span style={{ color: '#7c3aed', fontWeight: 600 }}> +{slot.linked_surveys.length - 1}</span>
            )}
          </>
        ) : <span style={{ color: '#bdbdbd' }}>Enquête non liée</span>}
      </div>

      {/* Client */}
      <div style={{ fontSize: '0.66rem', color: '#9e9e9e', marginBottom: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {project?.client?.name && (
          <><FontAwesomeIcon icon={faBuilding} size="xs" style={{ opacity: 0.35, marginRight: 4 }} />{project.client.name}</>
        )}
      </div>

      {/* Row 3: Assignees + status */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
        <span style={{ fontSize: '0.64rem', color: '#9e9e9e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center' }}>
          {(slot.assignees && slot.assignees.length > 0) ? (
            <><FontAwesomeIcon icon={faUser} size="xs" style={{ opacity: 0.35, marginRight: 5 }} />{slot.assignees.map(a => a.name).join(', ')}</>
          ) : (slot.wave ? slot.wave.name : '')}
        </span>
        {slot.is_finished ? (
          <span style={{ fontSize: '0.58rem', fontWeight: 600, padding: '2px 7px', borderRadius: 3, background: '#e6f4ea', color: '#137333' }}>
            Terminée
          </span>
        ) : (
          <span style={{ fontSize: '0.58rem', color: '#bdbdbd' }}>En cours</span>
        )}
      </div>
    </div>
  );
};

export default SlotCard;
